
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export function useSubscriptionActions() {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleCancelSubscription = async (subscriptionId: string, token?: string | null) => {
    try {
      setLoading(true);
      console.log('Attempting to cancel subscription:', subscriptionId);
      
      // Create the request body, including token if it exists
      const requestBody: any = { subscriptionId };
      if (token) {
        requestBody.token = token;
      }
      
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: requestBody
      });

      if (error) {
        console.error('Error cancelling subscription via Edge Function:', error);
        throw error;
      }

      if (!data.success) {
        throw new Error(data.message || 'Failed to cancel subscription');
      }

      console.log('Subscription cancelled successfully:', data);
      
      toast({
        title: "Success",
        description: "Subscription cancelled successfully",
      });
      
      return true;
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to cancel subscription",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleAutoRenewalToggle = async (subscriptionId: string, currentValue: boolean, token?: string | null): Promise<void> => {
    try {
      console.log('Updating auto-renewal setting:', subscriptionId, 'current value:', currentValue, 'new value:', !currentValue);
      
      // Create the request body, including token if it exists
      const requestBody: any = { 
        subscriptionId, 
        autoRenewal: !currentValue 
      };
      
      if (token) {
        requestBody.token = token;
      }
      
      const { data, error } = await supabase.functions.invoke('update-subscription-auto-renewal', {
        body: requestBody
      });
      
      if (error) {
        console.error('Error updating auto-renewal via Edge Function:', error);
        throw error;
      }
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to update auto-renewal');
      }
      
      console.log('Auto-renewal setting updated successfully');
    } catch (error) {
      console.error('Error updating auto-renewal setting:', error);
      throw error; // Re-throw the error so the component can handle it
    }
  };

  return {
    loading,
    handleCancelSubscription,
    handleAutoRenewalToggle
  };
}
